import { useEffect, useState, useMemo, useRef } from "react";
import RGL, { WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/MultiSelect";
import { ExcelTable } from "@/components/ExcelTable";
import { RapBadge, RapportinoDetailDialog } from "@/components/RapportinoDetail";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingUp, TrendingDown, Wallet, Inbox, GripVertical, RotateCcw } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const GridLayout = WidthProvider(RGL);

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtMonth = (m) => { const [y, mm] = (m || "").split("-"); return `${MESI[parseInt(mm, 10) - 1] || mm} ${(y || "").slice(2)}`; };
const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const marginCls = (n) => ((n || 0) < 0 ? "text-rose-600" : "text-emerald-600");

const KPI_META = {
  revenue: { label: "Ricavi", icon: TrendingUp, box: "bg-emerald-50 text-emerald-600", testid: "dash-revenue" },
  cost: { label: "Costi", icon: TrendingDown, box: "bg-rose-50 text-rose-600", testid: "dash-cost" },
  margin: { label: "Margine", icon: Wallet, box: "bg-blue-50 text-[#002FA7]", testid: "dash-margin" },
};

const LAYOUT_KEY = "dash_layout_v2";
const DEFAULT_LAYOUT = [
  { i: "revenue", x: 0, y: 0, w: 4, h: 3, minW: 2, minH: 3 },
  { i: "cost", x: 4, y: 0, w: 4, h: 3, minW: 2, minH: 3 },
  { i: "margin", x: 8, y: 0, w: 4, h: 3, minW: 2, minH: 3 },
  { i: "chart", x: 0, y: 3, w: 12, h: 11, minW: 4, minH: 6 },
  { i: "commessa", x: 0, y: 14, w: 6, h: 10, minW: 3, minH: 5 },
  { i: "operator", x: 6, y: 14, w: 6, h: 10, minW: 3, minH: 5 },
];

const commessaColumns = [
  { key: "commessa_name", header: "Commessa", sortable: true, className: "font-medium text-slate-900", cell: (r) => r.commessa_name },
  { key: "operator_name", header: "Operatore", sortable: true, className: "text-slate-700", cell: (r) => r.operator_name },
  { key: "count", header: "Rapportini", align: "right", type: "number", sortable: true, className: "text-right text-slate-600", cell: (r) => r.count },
  { key: "revenue", header: "Ricavi", align: "right", type: "number", sortable: true, className: "text-right text-emerald-700 font-semibold", cell: (r) => fmtEur(r.revenue) },
  { key: "cost", header: "Costi", align: "right", type: "number", sortable: true, className: "text-right text-rose-700", cell: (r) => fmtEur(r.cost) },
  { key: "margin", header: "Margine", align: "right", type: "number", sortable: true, className: "text-right font-heading font-bold", cell: (r) => <span className={marginCls(r.margin)}>{fmtEur(r.margin)}</span> },
];
const operatorColumns = [
  { key: "operator_name", header: "Operatore", sortable: true, className: "font-medium text-slate-900", cell: (r) => r.operator_name },
  { key: "commessa_name", header: "Commessa", sortable: true, className: "text-slate-700", cell: (r) => r.commessa_name },
  ...commessaColumns.slice(2),
];
const listColumns = [
  { key: "date_str", header: "Data", sortValue: (r) => new Date(r.date + "T00:00:00").getTime(), className: "text-slate-500", cell: (r) => r.date_str },
  { key: "operator_name", header: "Operatore", className: "font-medium text-slate-900", cell: (r) => r.operator_name },
  { key: "hours", header: "Ore", type: "number", className: "text-slate-600", cell: (r) => `${r.hours} h` },
  { key: "revenue", header: "Ricavi", type: "number", align: "right", className: "text-right text-emerald-700 font-semibold", cell: (r) => fmtEur(r.revenue) },
  { key: "cost_total", header: "Costi", type: "number", align: "right", className: "text-right text-rose-700", cell: (r) => fmtEur(r.cost_total) },
  { key: "margin", header: "Margine", type: "number", align: "right", className: "text-right font-heading font-bold", cell: (r) => <span className={marginCls(r.margin)}>{fmtEur(r.margin)}</span> },
  { key: "status_label", header: "Stato", cell: (r) => <RapBadge s={r.status} /> },
];

const TotalsRow = ({ rows }) => {
  const t = (rows || []).reduce((a, r) => ({ count: a.count + (r.count || 0), revenue: a.revenue + (r.revenue || 0), cost: a.cost + (r.cost || 0), margin: a.margin + (r.margin || 0) }), { count: 0, revenue: 0, cost: 0, margin: 0 });
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-slate-200 px-1 pt-3 text-sm" data-testid="dash-totals">
      <span className="mr-auto font-heading font-bold text-slate-800">Totali</span>
      <span className="text-slate-500">Rapportini: <b className="text-slate-800">{t.count}</b></span>
      <span className="text-emerald-700">Ricavi: <b>{fmtEur(t.revenue)}</b></span>
      <span className="text-rose-700">Costi: <b>{fmtEur(t.cost)}</b></span>
      <span className={marginCls(t.margin)}>Margine: <b>{fmtEur(t.margin)}</b></span>
    </div>
  );
};

export default function RapportiniDashboard() {
  const { user } = useAuth();
  const isOperator = user?.role === "utilizzatore";
  const [stats, setStats] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [operators, setOperators] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selCommesse, setSelCommesse] = useState([]);
  const [selOperators, setSelOperators] = useState([]);
  const [layout, setLayout] = useState(() => { try { return JSON.parse(localStorage.getItem(LAYOUT_KEY)) || DEFAULT_LAYOUT; } catch { return DEFAULT_LAYOUT; } });
  const readyRef = useRef(false);
  const saveTimer = useRef(null);
  const [group, setGroup] = useState(null);
  const [groupRaps, setGroupRaps] = useState([]);
  const [rapDetail, setRapDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data));
    api.get("/team-members").then((r) => setOperators(r.data));
    api.get("/me/dashboard-layout").then(({ data }) => {
      if (Array.isArray(data.layout) && data.layout.length) { setLayout(data.layout); localStorage.setItem(LAYOUT_KEY, JSON.stringify(data.layout)); }
    }).catch(() => {}).finally(() => { readyRef.current = true; });
  }, []);
  useEffect(() => {
    const p = new URLSearchParams();
    if (from) p.append("date_from", from);
    if (to) p.append("date_to", to);
    selCommesse.forEach((id) => p.append("commessa_ids", id));
    selOperators.forEach((id) => p.append("operator_ids", id));
    api.get(`/rapportini/stats?${p.toString()}`).then((r) => setStats(r.data)).catch(() => {});
  }, [from, to, selCommesse, selOperators, refreshKey]);

  useEffect(() => {
    if (!group) return;
    const p = new URLSearchParams();
    if (from) p.append("date_from", from);
    if (to) p.append("date_to", to);
    api.get(`/rapportini?${p.toString()}`).then((r) => {
      const list = r.data.filter((x) => (x.commessa_name || "—") === group.commessa_name && (x.operator_name || "—") === group.operator_name);
      setGroupRaps(list.map((x) => ({ ...x, date_str: fmtDate(x.date), status_label: x.status === "trasmesso" ? "Trasmesso" : "Bozza" })));
    }).catch(() => setGroupRaps([]));
  }, [group, from, to, refreshKey]);
  const openGroup = (row) => setGroup({ commessa_name: row.commessa_name || "—", operator_name: row.operator_name || "—" });

  const saveLayout = (l) => {
    const clean = l.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
    setLayout((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(clean)) return prev;
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(clean));
      if (readyRef.current) { clearTimeout(saveTimer.current); saveTimer.current = setTimeout(() => { api.put("/me/dashboard-layout", { layout: clean }).catch(() => {}); }, 800); }
      return clean;
    });
  };
  const resetLayout = () => { localStorage.removeItem(LAYOUT_KEY); setLayout(DEFAULT_LAYOUT); api.put("/me/dashboard-layout", { layout: [] }).catch(() => {}); };

  const kpiNode = (k) => {
    const M = KPI_META[k];
    return (
      <div className="flex h-full items-center gap-3" data-testid={`dash-kpi-${k}`}>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${M.box}`}><M.icon className="h-6 w-6" /></div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-400">{M.label}</p>
          <p className={`font-heading text-2xl font-extrabold whitespace-nowrap ${k === "margin" ? marginCls(stats?.margin) : "text-slate-900"}`} data-testid={M.testid}>{fmtEur(stats?.[k])}</p>
        </div>
      </div>
    );
  };

  const chartNode = (
    (stats?.by_month || []).length === 0 ? (
      <p className="text-sm text-slate-400">Nessun dato nel periodo selezionato.</p>
    ) : (
      <div className="h-full min-h-[220px] w-full" data-testid="dash-monthly-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stats.by_month.map((m) => ({ ...m, label: fmtMonth(m.month) }))} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickFormatter={(v) => `€${v}`} width={70} />
            <Tooltip formatter={(v) => fmtEur(v)} cursor={{ fill: "rgba(0,47,167,0.05)" }} />
            <Legend />
            <Bar dataKey="revenue" name="Ricavi" fill="#059669" radius={[4, 4, 0, 0]} />
            <Bar dataKey="cost" name="Costi" fill="#e11d48" radius={[4, 4, 0, 0]} />
            <Bar dataKey="margin" name="Margine" fill="#002FA7" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  );

  const crossData = useMemo(() => stats?.by_commessa_operator || [], [stats]);
  const commessaNode = <ExcelTable data={crossData} testid="dash-by-commessa" onRowClick={openGroup} rowTestId={(r) => "dash-commessa-row-" + ((r.commessa_name || "") + "-" + (r.operator_name || "")).toLowerCase().replace(/[^a-z0-9]+/g, "-")} initialSort={{ key: "commessa_name", dir: "asc" }} emptyIcon={Inbox} emptyText="Nessun dato nel periodo" columns={commessaColumns} footer={(rows) => <TotalsRow rows={rows} />} />;
  const operatorNode = <ExcelTable data={crossData} testid="dash-by-operator" onRowClick={openGroup} rowTestId={(r) => "dash-operator-row-" + ((r.operator_name || "") + "-" + (r.commessa_name || "")).toLowerCase().replace(/[^a-z0-9]+/g, "-")} initialSort={{ key: "operator_name", dir: "asc" }} emptyIcon={Inbox} emptyText="Nessun dato nel periodo" columns={operatorColumns} footer={(rows) => <TotalsRow rows={rows} />} />;

  const tiles = [
    { i: "revenue", title: "Ricavi", node: kpiNode("revenue") },
    { i: "cost", title: "Costi", node: kpiNode("cost") },
    { i: "margin", title: "Margine", node: kpiNode("margin") },
    { i: "chart", title: "Andamento nell'intervallo temporale selezionato", node: chartNode },
    { i: "commessa", title: "Dettaglio per commessa", node: commessaNode },
  ];
  if (!isOperator) tiles.push({ i: "operator", title: "Dettaglio per operatore", node: operatorNode });

  const gridLayout = useMemo(() => {
    const ids = ["revenue", "cost", "margin", "chart", "commessa"].concat(isOperator ? [] : ["operator"]);
    return ids.map((i) => { const d = DEFAULT_LAYOUT.find((x) => x.i === i); const l = layout.find((x) => x.i === i); return l ? { ...d, ...l } : d; });
  }, [layout, isOperator]);

  const Tile = ({ title, children }) => (
    <Card className="flex h-full flex-col overflow-hidden rounded-lg border-slate-200 shadow-sm">
      <div className="tile-drag flex cursor-move items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500 select-none">
        <GripVertical className="h-3.5 w-3.5 text-slate-400" /> {title}
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
    </Card>
  );

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Dashboard rapportini</h1>
          <p className="mt-1 text-sm text-slate-500">Ricavi, costi e margine nel periodo selezionato.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs text-slate-500">Dal</Label><Input type="date" data-testid="dash-from" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 w-40" /></div>
          <div><Label className="text-xs text-slate-500">Al</Label><Input type="date" data-testid="dash-to" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="mt-1.5 w-40" /></div>
          <div>
            <Label className="block text-xs text-slate-500">Commessa</Label>
            <div className="mt-1.5 w-56"><MultiSelect testid="dash-commessa" options={commesse.map((c) => ({ value: c.id, label: c.name, keywords: c.code }))} value={selCommesse} onChange={setSelCommesse} allLabel="Tutte le commesse" placeholder="Cerca commessa..." /></div>
          </div>
          {!isOperator && (
            <div>
              <Label className="block text-xs text-slate-500">Operatore</Label>
              <div className="mt-1.5 w-56"><MultiSelect testid="dash-operatore" options={operators.map((o) => ({ value: o.id, label: o.name }))} value={selOperators} onChange={setSelOperators} allLabel="Tutti gli operatori" placeholder="Cerca operatore..." /></div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop: griglia trascinabile e ridimensionabile */}
      <div className="hidden lg:block">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs text-slate-400">Trascina dall'intestazione per spostare le tile; trascina l'angolo in basso a destra per ridimensionarle.</p>
          <Button variant="outline" size="sm" onClick={resetLayout} data-testid="dash-reset-layout" className="rounded-md"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Ripristina layout</Button>
        </div>
        <GridLayout className="layout" layout={gridLayout} cols={12} rowHeight={30} margin={[16, 16]} draggableHandle=".tile-drag" isBounded compactType="vertical" onDragStop={saveLayout} onResizeStop={saveLayout} data-testid="dash-grid">
          {tiles.map((t) => (
            <div key={t.i} data-testid={`dash-tile-${t.i}`}>
              <Tile title={t.title}>{t.node}</Tile>
            </div>
          ))}
        </GridLayout>
      </div>

      {/* Mobile/tablet: layout impilato */}
      <div className="space-y-6 lg:hidden">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {["revenue", "cost", "margin"].map((k) => (
            <Card key={k} className="rounded-lg border-slate-200 p-5 shadow-sm">{kpiNode(k)}</Card>
          ))}
        </div>
        <Card className="rounded-lg border-slate-200 p-5 shadow-sm">
          <h2 className="mb-4 font-heading font-bold text-slate-900">Andamento nell'intervallo temporale selezionato</h2>
          <div style={{ height: 320 }}>{chartNode}</div>
        </Card>
        <Card className="rounded-lg border-slate-200 shadow-sm"><div className="p-4"><h2 className="mb-3 font-heading font-bold text-slate-900">Dettaglio per commessa</h2>{commessaNode}</div></Card>
        {!isOperator && <Card className="rounded-lg border-slate-200 shadow-sm"><div className="p-4"><h2 className="mb-3 font-heading font-bold text-slate-900">Dettaglio per operatore</h2>{operatorNode}</div></Card>}
      </div>

      <Dialog open={!!group} onOpenChange={(o) => !o && setGroup(null)}>
        <DialogContent className="max-w-3xl" data-testid="dash-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rapportini · {group?.commessa_name}</DialogTitle>
            <DialogDescription>{group?.operator_name} · {groupRaps.length} {groupRaps.length === 1 ? "rapportino" : "rapportini"} nel periodo · clicca una riga per il dettaglio</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            <ExcelTable data={groupRaps} testid="dash-group-table" rowTestId={(r) => `dash-group-rap-${r.id}`} onRowClick={(r) => setRapDetail(r)} initialSort={{ key: "date_str", dir: "desc" }} emptyIcon={Inbox} emptyText="Nessun rapportino nel periodo" columns={listColumns} />
          </div>
        </DialogContent>
      </Dialog>

      <RapportinoDetailDialog detail={rapDetail} open={!!rapDetail} onClose={() => setRapDetail(null)} onChanged={() => setRefreshKey((k) => k + 1)} backLabel="Torna all'elenco" />
    </div>
  );
}
